import { useState, useEffect, useRef } from 'react';
import { useFormikContext } from 'formik';
import { useSelector, useDispatch } from 'react-redux';
import dayjs from 'dayjs';
import moment from 'moment';
import { Checkbox, Steps, Tag, Tooltip as AntTooltip, Spin, message } from 'antd';
import { InputField, SelectInput, DatePickerField, AntdButton } from '../../../blueplanitUtils/InputField';
import { Form, Typography, Row, Col } from 'antd';
import { getOptionsByPath } from '../../../utilsdata';
import { LEAD_INFORMATION, LEAD_STAGE, LEAD_STATUS, LEAD_CHANNEL, PRIORITY } from '../../../Dropdownmanager';


const ProtectedLeadInformation = ({
  SalesInfo,
  assignToUsers,
  loginUser,
  formatDateTime,
  assignHandler,
  getAssignToUsers,
  getDateTimeValue
}) => {
  const { values, handleChange, setFieldValue } = useFormikContext();
  const dispatch = useDispatch();
  const NestedDatabucket = useSelector((state) => state.dataBucket.NestedDatabucket);

  const isInitializedRef = useRef(false);
  const userEditedFieldsRef = useRef(new Set());
  const previousBackendValuesRef = useRef({});

  const [localValues, setLocalValues] = useState(() => ({
    lastContactedDate: values.lastContactedDate,
    followup: values.followup,
    followupDate: values.followupDate,
    callscheduledwithOPs: values.callscheduledwithOPs,
    assign: values.assign,
    assignedTo: values.assignedTo,
    assignTo: values.assignTo,
    priority: values.priority,
    status: values.status,
    stage: values.stage,
    channel: values.channel,
    successfull: values.successfull,
    isInPipeline: values.isInPipeline || false,
    pipelineStage: values.pipelineStage || '',
    lostReason: values.lostReason || '',
    dealValue: values.dealValue || '',
    assignedAt: values.assignedAt || null,
    isAssignLocked: values.isAssignLocked || false,
    assignLockedAt: values.assignLockedAt || null,
    assignUnlockAt: values.assignUnlockAt || null,
  }));

  const isAdmin = loginUser?.role === "Admin" || loginUser?.role === "Super Admin";
  const currentUserFullName = `${loginUser?.firstname} ${loginUser?.lastname}`;
  const isOwner = localValues.assignedTo === currentUserFullName || localValues.assignTo === loginUser?.id;
  const isLockedByOther = localValues.isAssignLocked && localValues.assignedTo && !isOwner;
  const canModifyAssignment = !localValues.isAssignLocked || isAdmin || isOwner;

  const [current, setCurrent] = useState(true);
  const [close, setClose] = useState(!values?.lastContactedDate ? true : false);

  // Initial load - only run once
  useEffect(() => {
    if (!isInitializedRef.current) {
      const initialValues = {
        lastContactedDate: values.lastContactedDate,
        followup: values.followup,
        followupDate: values.followupDate,
        callscheduledwithOPs: values.callscheduledwithOPs,
        assign: values.assign,
        assignedTo: values.assignedTo,
        assignTo: values.assignTo,
        priority: values.priority,
        status: values.status,
        stage: values.stage,
        channel: values.channel,
        successfull: values.successfull,
        isInPipeline: values.isInPipeline ?? SalesInfo?.isInPipeline ?? false,
        pipelineStage: values.pipelineStage || SalesInfo?.pipelineStage || '',
        lostReason: values.lostReason || SalesInfo?.lostReason || '',
        dealValue: values.dealValue || SalesInfo?.dealValue || '',
        assignedAt: values.assignedAt || SalesInfo?.assignedAt || null,
        isAssignLocked: values.isAssignLocked ?? SalesInfo?.isAssignLocked ?? false,
        assignLockedAt: values.assignLockedAt || SalesInfo?.assignLockedAt || null,
        assignUnlockAt: values.assignUnlockAt || SalesInfo?.assignUnlockAt || null,
      };
      setLocalValues(initialValues);
      previousBackendValuesRef.current = { ...initialValues };
      isInitializedRef.current = true;
    }
  }, []);

  // Detect backend data changes and merge intelligently
  useEffect(() => {
    if (!isInitializedRef.current) return;

    const fieldsToTrack = [
      'lastContactedDate',
      'followup',
      'followupDate',
      'callscheduledwithOPs',
      'assign',
      'assignedTo',
      'assignTo',
      'priority',
      'status',
      'stage',
      'channel',
      'successfull',
      'isInPipeline',
      'pipelineStage',
      'lostReason',
      'dealValue',
      'assignedAt',
      'isAssignLocked',
      'assignLockedAt',
      'assignUnlockAt',
    ];

    const currentBackendValues = {
      lastContactedDate: values.lastContactedDate,
      followup: values.followup,
      followupDate: values.followupDate,
      callscheduledwithOPs: values.callscheduledwithOPs,
      assign: values.assign,
      assignedTo: values.assignedTo,
      assignTo: values.assignTo,
      priority: values.priority,
      status: values.status,
      stage: values.stage,
      channel: values.channel,
      successfull: values.successfull,
      isInPipeline: values.isInPipeline ?? false,
      pipelineStage: values.pipelineStage || '',
      lostReason: values.lostReason || '',
      dealValue: values.dealValue || '',
      assignedAt: values.assignedAt || null,
      isAssignLocked: values.isAssignLocked ?? false,
      assignLockedAt: values.assignLockedAt || null,
      assignUnlockAt: values.assignUnlockAt || null,
    };

    // Check if backend data has actually changed
    let backendChanged = false;
    fieldsToTrack.forEach(field => {
      if (JSON.stringify(currentBackendValues[field]) !== JSON.stringify(previousBackendValuesRef.current[field])) {
        backendChanged = true;
      }
    });

    if (backendChanged) {
      // Merge backend data with user edits
      setLocalValues(prev => {
        const merged = { ...prev };

        fieldsToTrack.forEach(field => {
          const userEdited = userEditedFieldsRef.current.has(field);

          if (userEdited) {
            // Keep user's edit
            merged[field] = prev[field];
          } else {
            // Use backend data for unedited fields
            merged[field] = currentBackendValues[field];
          }
        });

        return merged;
      });

      previousBackendValuesRef.current = { ...currentBackendValues };
    }
  }, [values]);

  // **CRITICAL FIX**: Sync localValues back to Formik whenever they change
  useEffect(() => {
    if (!isInitializedRef.current) return;

    // Update Formik values with local values
    Object.keys(localValues).forEach(key => {
      if (JSON.stringify(values[key]) !== JSON.stringify(localValues[key])) {
        setFieldValue(key, localValues[key], false); // false = don't validate immediately
      }
    });
  }, [localValues, setFieldValue]);

  const markFieldAsEdited = (fieldName) => {
    userEditedFieldsRef.current.add(fieldName);
  };

  const handleFieldChange = (fieldName, value) => {
    markFieldAsEdited(fieldName);
    setLocalValues(prev => ({ ...prev, [fieldName]: value }));
  };

  const handleAssignChange = (checked) => {
    markFieldAsEdited('assign');
    markFieldAsEdited('assignedTo');
    markFieldAsEdited('assignTo');
    markFieldAsEdited('assignedAt');

    const newAssignedTo = checked ? `${loginUser?.firstname} ${loginUser?.lastname}` : '';
    const newAssignTo = checked ? `${loginUser?.firstname} ${loginUser?.lastname}` : '';

    setLocalValues(prev => ({
      ...prev,
      assign: checked,
      assignedTo: newAssignedTo,
      assignTo: newAssignTo,
      assignedAt: checked ? Date.now() + "" : null,
      // When unassigning, also unlock
      ...(checked ? {} : { isAssignLocked: false, assignLockedAt: null, assignUnlockAt: null }),
    }));

    if (!checked) {
      markFieldAsEdited('isAssignLocked');
      markFieldAsEdited('assignLockedAt');
      markFieldAsEdited('assignUnlockAt');
    }
  };

  useEffect(() => {
    setClose(localValues.lastContactedDate ? false : true);
  }, [localValues.lastContactedDate]);

  useEffect(() => {
    if (!localValues.followup) {
      handleFieldChange('followupDate', null);
    }
  }, [localValues.followup]);


  return (
    <div className="lead-info">

      {/* HEADER */}
      <div className="lead-info-header">
        <div className="lead-info-title">

          {/* Title row: "Lead Information" + pipeline toggle */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
            <Typography.Title level={5} style={{ margin: 0 }}>
              Lead Information
            </Typography.Title>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <AntdButton
                label={localValues.isInPipeline ? "In Pipeline" : "Add to Pipeline"}
                type="default"
                size="small"
                style={{
                  height: 24, lineHeight: '22px', padding: '0 12px', fontSize: 12,
                  borderRadius: 16, display: 'flex', alignItems: 'center',
                  backgroundColor: localValues.isInPipeline ? '#1677ff' : '#fff',
                  borderColor: localValues.isInPipeline ? '#1677ff' : '#d9d9d9',
                  color: localValues.isInPipeline ? '#fff' : '#000',
                  whiteSpace: 'nowrap',
                }}
                onClick={() => handleFieldChange('isInPipeline', !localValues.isInPipeline)}
              />
              {localValues.isAssignLocked && (
                <Tag color="error" style={{ margin: 0, fontSize: 12, borderRadius: 16 }}>Locked</Tag>
              )}
            </div>
          </div>

          {/* Lock / Assignment status bar */}
          {localValues.assign && (() => {
            const parseDate = (val) => {
              if (!val) return null;
              const asNum = Number(val);
              return !isNaN(asNum) && asNum > 1e12 ? dayjs(asNum) : dayjs(val);
            };
            const assignedDate = parseDate(localValues.assignedAt);
            const daysSinceAssigned = assignedDate?.isValid() ? dayjs().diff(assignedDate, 'day') : null;
            const daysUntilUnassign = daysSinceAssigned !== null ? 30 - daysSinceAssigned : null;

            if (localValues.isAssignLocked) {
              const lockedDate = parseDate(localValues.assignLockedAt);
              const daysLocked = lockedDate?.isValid() ? dayjs().diff(lockedDate, 'day') : null;
              return (
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 4, padding: '4px 8px', background: '#fff1f0', border: '1px solid #ffa39e', borderRadius: 6, fontSize: 12 }}>
                  <span style={{ color: '#cf1322' }}>
                    Locked {daysLocked !== null ? `for ${daysLocked} day${daysLocked !== 1 ? 's' : ''}` : ''} — will not auto-unassign
                  </span>
                  {assignedDate && (
                    <span style={{ color: '#595959' }}>
                      Assigned: {assignedDate.format('DD MMM YYYY')}
                    </span>
                  )}
                </div>
              );
            }

            if (assignedDate && daysUntilUnassign !== null) {
              const isUrgent = daysUntilUnassign <= 7;
              return (
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 4, padding: '4px 8px', background: isUrgent ? '#fffbe6' : '#f6ffed', border: `1px solid ${isUrgent ? '#ffe58f' : '#b7eb8f'}`, borderRadius: 6, fontSize: 12 }}>
                  <span style={{ color: isUrgent ? '#d48806' : '#389e0d' }}>
                    {daysUntilUnassign > 0
                      ? `Auto-unassigns in ${daysUntilUnassign} day${daysUntilUnassign !== 1 ? 's' : ''}`
                      : 'Auto-unassign overdue — will be unassigned by next cron run'}
                  </span>
                  <span style={{ color: '#595959' }}>
                    Assigned: {assignedDate.format('DD MMM YYYY')}
                  </span>
                </div>
              );
            }
            return null;
          })()}

          {/* Meta row: ID · Created · Updated */}
          <div className="lead-info-row" style={{ marginTop: 4 }}>
            <div><b>ID:&nbsp;</b>{SalesInfo?.leadID || "-"}</div>
            <div><b>Created Date:&nbsp;</b>{SalesInfo?.createdDate ? formatDateTime(SalesInfo.createdDate) : "-"}</div>
            <div><b>Updated Date:&nbsp;</b>{SalesInfo?.lastUpdatedDate ? formatDateTime(SalesInfo.lastUpdatedDate) : "-"}</div>
          </div>

          {/* Pipeline detail fields */}
          {localValues.isInPipeline && (() => {
            const PIPE_STEPS = ['Prospect', 'Qualified', 'Proposal', 'Negotiation', 'Onboarding', 'Closed Won'];
            const isLost = localValues.pipelineStage === 'Closed Lost';
            const currentStep = isLost ? -1 : PIPE_STEPS.indexOf(localValues.pipelineStage);
            return (
              <div style={{
                marginTop: 8, background: '#f0f9ff', border: '1px solid #bae6fd',
                borderRadius: 8, padding: '10px 14px',
              }}>
                {/* Deal value + lost reason row */}
                <div style={{ display: 'flex', gap: 8, marginBottom: 10, flexWrap: 'wrap', alignItems: 'center' }}>
                  <InputField
                    placeholder="Deal Value ($)"
                    value={localValues.dealValue}
                    onChange={(e) => handleFieldChange('dealValue', e.target.value)}
                    style={{ width: 130 }}
                    copyable
                  />
                  <AntTooltip title={isLost ? 'Click to re-open' : 'Mark as Closed Lost'}>
                    <Tag
                      color={isLost ? 'error' : 'default'}
                      style={{ cursor: 'pointer', fontSize: 12, padding: '2px 10px', borderRadius: 12 }}
                      onClick={() => handleFieldChange('pipelineStage', isLost ? 'Prospect' : 'Closed Lost')}
                    >
                      {isLost ? '✕ Closed Lost' : 'Mark Lost'}
                    </Tag>
                  </AntTooltip>
                  {isLost && (
                    <InputField
                      placeholder="Lost reason"
                      value={localValues.lostReason}
                      onChange={(e) => handleFieldChange('lostReason', e.target.value)}
                      style={{ width: 200 }}
                      copyable
                    />
                  )}
                </div>
                {/* Stage stepper */}
                <Steps
                  size="small"
                  current={isLost ? 3 : currentStep}
                  status={isLost ? 'error' : 'process'}
                  style={{ fontSize: 12 }}
                  onChange={(step) => handleFieldChange('pipelineStage', PIPE_STEPS[step])}
                  items={PIPE_STEPS.map((stage) => ({ title: stage }))}
                />
              </div>
            );
          })()}

        </div>
      </div>

      {/* MAIN ROW */}
      <Row gutter={[6, 0]} align="stretch" style={{ marginTop: "8px" }}>

        {/* LEFT BLOCK */}
        <Col span={12}>
          <div className="lead-card">

            <div className="date-field">
              <label className="date-label lastcommunicated">Last Communicated</label>
              <div className="date-picker-with-actions">
                <DatePickerField
                  showTime inputReadOnly format="DD/MM/YYYY hh:mm A"
                  value={close ? null : getDateTimeValue(localValues.lastContactedDate)}
                  onChange={(date) => { handleFieldChange("lastContactedDate", date); setClose(false); setCurrent(true); }}
                  disabledDate={(current) => current && current >= moment().endOf("day")}
                  allowClear={false} className="date-input"
                />
                <div className="date-actions">
                  <span className="date-action"
                    onClick={() => { handleFieldChange("lastContactedDate", dayjs()); setClose(false); setCurrent(true); }}>
                    Current
                  </span>
                  <span className="date-action clear"
                    onClick={() => { handleFieldChange("lastContactedDate", null); setClose(true); }}>
                    Clear
                  </span>
                </div>
              </div>
            </div>

            <div className="checkbox-field">
              <label className="checkbox-label">Call Scheduled</label>
              <Checkbox checked={localValues.callscheduledwithOPs}
                onChange={(e) => handleFieldChange("callscheduledwithOPs", e.target.checked)}
                className="checkbox-up" />
            </div>

            <div className="checkbox-field">
              <label className="checkbox-label">Follow-Up</label>
              <Checkbox checked={localValues.followup}
                onChange={(e) => handleFieldChange("followup", e.target.checked)}
                className="checkbox-up" />
            </div>

            <div className="date-field followup-date-up">
              <label className="date-label lastcommunicated">Follow-Up Date</label>
              <DatePickerField
                inputReadOnly format="DD/MM/YYYY"
                value={localValues.followup ? getDateTimeValue(localValues.followupDate) : null}
                onChange={(date) => handleFieldChange("followupDate", date)}
                disabled={!localValues.followup} className="date-input"
              />
            </div>

          </div>
        </Col>

        {/* RIGHT BLOCK */}
        <Col span={12}>
          <div className="lead-card">

            <div className="rb-checkbox-row" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span className="rb-checkbox-label">{localValues.assign ? "Un-Assign" : "Assign"}</span>
                <AntTooltip title={!canModifyAssignment ? "This lead is locked. Only an Admin can reassign." : ""}>
                  <Checkbox checked={localValues.assign}
                    disabled={!canModifyAssignment}
                    onChange={(e) => handleAssignChange(e.target.checked)} />
                </AntTooltip>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <AntTooltip title={
                  isLockedByOther && !isAdmin
                    ? "This lead is locked by another user. Contact an Admin to unlock."
                    : localValues.isAssignLocked
                      ? "Assignment locked permanently. Uncheck to unlock."
                      : "Lock this lead's assignment (no one can reassign until unlocked)"
                }>
                  <Checkbox
                    checked={localValues.isAssignLocked}
                    disabled={isLockedByOther}
                    onChange={(e) => {
                      markFieldAsEdited("isAssignLocked");
                      markFieldAsEdited("assignLockedAt");
                      markFieldAsEdited("assignUnlockAt");
                      setLocalValues((prev) => ({
                        ...prev,
                        isAssignLocked: e.target.checked,
                        assignLockedAt: e.target.checked ? Date.now() + "" : null,
                        assignUnlockAt: null,
                      }));
                    }}
                  >
                    <span style={{ fontSize: 12, whiteSpace: 'nowrap', color: localValues.isAssignLocked ? '#cf1322' : '#595959' }}>
                      {localValues.isAssignLocked ? "🔒 Locked" : "🔓 Lock"}
                    </span>
                  </Checkbox>
                </AntTooltip>
                {isLockedByOther && isAdmin && (
                  <AntTooltip title="Admin: Force unlock this lead">
                    <Tag
                      color="warning"
                      style={{ cursor: 'pointer', fontSize: 11, margin: 0, borderRadius: 12 }}
                      onClick={() => {
                        markFieldAsEdited("isAssignLocked");
                        markFieldAsEdited("assignLockedAt");
                        markFieldAsEdited("assignUnlockAt");
                        setLocalValues((prev) => ({
                          ...prev,
                          isAssignLocked: false,
                          assignLockedAt: null,
                          assignUnlockAt: null,
                        }));
                        message.success("Lead unlocked by Admin");
                      }}
                    >
                      Admin Unlock
                    </Tag>
                  </AntTooltip>
                )}
              </div>
            </div>

            <div className="rb-field-row">
              <label className="rb-label">Assigned To</label>
              <InputField disabled value={localValues.assignedTo} className="rb-input" />
            </div>

            <div className="rb-field-row">
              <label className="rb-label assignto">Assign To</label>
              <div className="rb-input">
                <AntTooltip title={!canModifyAssignment ? "This lead is locked. Only an Admin can reassign." : ""}>
                  <SelectInput
                    value={localValues.assignTo}
                    disabled={!canModifyAssignment}
                    onChange={(value) => {
                      markFieldAsEdited("assignTo"); markFieldAsEdited("assignedTo"); markFieldAsEdited("assign"); markFieldAsEdited("assignedAt");
                      setLocalValues((prev) => ({ ...prev, assignTo: value, assignedTo: value, assign: true, assignedAt: Date.now() + "" }));
                    }}
                    options={getAssignToUsers(assignToUsers)}
                    placeholder="Assign To"
                  />
                </AntTooltip>
              </div>
            </div>

            <div className="rb-field-priority-row">
              <label className="rb-priority-label priority">Priority</label>
              <div className="rb-input">
                <SelectInput
                  value={localValues.priority}
                  onChange={(value) => handleFieldChange("priority", value)}
                  options={getOptionsByPath(NestedDatabucket, [LEAD_INFORMATION, PRIORITY])}
                />
              </div>
            </div>

          </div>
        </Col>

        {/* LEFT SMALL CARD */}
        <Col span={12}>
          <div className="lead-small-card">
            <div className="field-row">
              <label className="field-label leadsta">Lead Stage</label>
              <div className="field-input stageinput fixed-width" style={{ marginLeft: '-15px' }}>
                <SelectInput value={localValues.stage}
                  onChange={(value) => handleFieldChange("stage", value)}
                  options={getOptionsByPath(NestedDatabucket, [LEAD_INFORMATION, LEAD_STAGE])} />
              </div>
            </div>
            <div className="field-row leadchannel" style={{ marginTop: '10px' }}>
              <label className="field-label leadstage">Lead Channel</label>
              <div className="field-input fixed-width" style={{ marginLeft: '-15px' }}>
                <SelectInput value={localValues.channel}
                  onChange={(value) => handleFieldChange("channel", value)}
                  options={getOptionsByPath(NestedDatabucket, [LEAD_INFORMATION, LEAD_CHANNEL])} />
              </div>
            </div>
          </div>
        </Col>

        {/* RIGHT SMALL CARD */}
        <Col span={12}>
          <div className="lead-small-card">
            <div className="checkbox-row">
              <span className="checkbox-label">Lead Successful</span>
              <Checkbox checked={localValues.successfull}
                onChange={(e) => handleFieldChange("successfull", e.target.checked)}
                className="checkbox-up" />
            </div>
            <div className="field-status-row leadstatus">
              <label className="field-label leadstatus" style={{ marginBottom: '15px' }}>Lead Status</label>
              <div className="field-status-input">
                <SelectInput value={localValues.status}
                  onChange={(value) => handleFieldChange("status", value)}
                  options={getOptionsByPath(NestedDatabucket, [LEAD_INFORMATION, LEAD_STATUS])} />
              </div>
            </div>
          </div>
        </Col>

      </Row>
    </div>
  );
};

export default ProtectedLeadInformation;